import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Network, Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

function GoogleButton({ label }) {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      onClick={handleGoogle}
      data-testid="google-login-button"
      className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg bg-white text-slate-800 font-medium text-sm hover:bg-slate-100 transition-colors"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      {label}
    </button>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate(next ? decodeURIComponent(next) : "/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F17]">
      <div className="hidden lg:flex w-1/2 relative border-r border-[#1E2A3F] niq-grid-bg overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B0F17]" />
        <div className="relative z-10 flex flex-col justify-center px-16">
          <Link to="/" className="inline-flex mb-8"><img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-10 w-auto" /></Link>
          <h1 className="font-display text-4xl font-bold leading-tight mb-4">Your network,<br/>finally searchable.</h1>
          <p className="text-slate-400 max-w-md leading-relaxed">Turn any contacts file (CSV, Excel or ZIP) into a clean, AI-categorized, searchable list. Ask it your way. Every label shown with a simple quality flag. Never scraped.</p>
          <div className="mt-8 space-y-3 font-mono-niq text-xs text-slate-500">
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> CSV, Excel or ZIP · your data stays private</div>
            <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> High / Medium / Low quality flag on every label</div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="lg:hidden inline-flex mb-8"><img src="/linkviewer-logo.png" alt="LinkViewer.PRO — home" className="h-7 w-auto" /></Link>
          <h2 className="font-display text-2xl font-semibold mb-1">Sign in</h2>
          <p className="text-slate-500 text-sm mb-8">Sign in to your dashboard</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="label-mono text-slate-500 block mb-2">Email</label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 rounded-lg bg-[#111726] border border-[#27334D] text-sm focus:border-blue-500 focus:outline-none transition-colors" placeholder="you@company.com" />
              </div>
            </div>
            <div>
              <label className="label-mono text-slate-500 block mb-2">Password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-3 rounded-lg bg-[#111726] border border-[#27334D] text-sm focus:border-blue-500 focus:outline-none transition-colors" placeholder="••••••••" />
              </div>
            </div>
            <button data-testid="login-submit-button" disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 font-medium text-sm transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-[#1E2A3F]" /><span className="label-mono text-slate-600">or</span><div className="flex-1 h-px bg-[#1E2A3F]" /></div>
          <GoogleButton label="Continue with Google" />

          <p className="text-center text-sm text-slate-500 mt-8">
            No account? <Link to="/register" className="text-blue-400 hover:text-blue-300">Create one</Link>
          </p>
          <p className="text-center text-xs text-slate-600 mt-4 font-mono-niq">demo@networkiq.app · Demo1234!</p>
        </div>
      </div>
    </div>
  );
}
